import React, { useState, useEffect } from "react";
import {
  Modal,
  Select,
  Input,
  Switch,
  DatePicker,
  TimePicker,
  Spin,
} from "antd";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch } from "../../../../core/data/redux/store";
import {
  fetchAvailableInterviewers,
  editInterview,
  fetchInterviews,
} from "../../../../core/data/redux/recruitment/thunks";
import {
  selectAvailableInterviewers,
  selectAvailableInterviewersLoading,
} from "../../../../core/data/redux/recruitment/selectors";
import type { Interview } from "../../../../core/data/redux/recruitment/types";

const { TextArea } = Input;

type InterviewMode = "video" | "in_person" | "phone";

interface EditInterviewModalProps {
  show: boolean;
  onClose: () => void;
  interview: Interview | null;
}

const EditInterviewModal: React.FC<EditInterviewModalProps> = ({
  show,
  onClose,
  interview,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [loading, setLoading] = useState(false);

  // Form fields
  const [interviewDate, setInterviewDate] = useState<string>("");
  const [interviewTime, setInterviewTime] = useState<string>("");
  const [durationMinutes, setDurationMinutes] = useState<number>(60);
  const [interviewMode, setInterviewMode] = useState<InterviewMode>("video");
  const [interviewerIds, setInterviewerIds] = useState<number[]>([]);
  const [interviewLink, setInterviewLink] = useState("");
  const [interviewLocation, setInterviewLocation] = useState("");
  const [interviewPhone, setInterviewPhone] = useState("");
  const [instructions, setInstructions] = useState("");
  const [sendEmail, setSendEmail] = useState(true);

  const employeeOptions = useSelector(selectAvailableInterviewers).map(
    (u: any) => ({
      value: u.user_id,
      label: u.name || `${u.fname || ""} ${u.lname || ""}`.trim(),
    }),
  );
  const employeesLoading = useSelector(selectAvailableInterviewersLoading);

  useEffect(() => {
    if (show && interview) {
      dispatch(fetchAvailableInterviewers({}));

      if (interview.scheduled_at) {
        setInterviewDate(dayjs(interview.scheduled_at).format("YYYY-MM-DD"));
        setInterviewTime(dayjs(interview.scheduled_at).format("HH:mm:ss"));
      } else {
        setInterviewDate("");
        setInterviewTime("");
      }
      setDurationMinutes(interview.duration_minutes || 60);
      setInterviewMode((interview.interview_mode as InterviewMode) || "video");
      setInterviewerIds(
        interview.interviewer_ids || 
        interview.interviewers?.map((i) => i.user_id) || 
        []
      );
      setInterviewLink(interview.meeting_link || "");
      setInterviewLocation(interview.meeting_location || "");
      setInterviewPhone(
        interview.meeting_platform === "phone"
          ? interview.meeting_location || ""
          : "",
      );
      setInstructions(interview.instructions || "");
      setSendEmail(true);
    }
  }, [show, interview, dispatch]);

  const handleSubmit = async () => {
    if (!interview) return;
    setLoading(true);

    try {
      let scheduled_at = undefined;
      if (interviewDate && interviewTime) {
        scheduled_at = `${interviewDate}T${interviewTime}`;
      }

      await dispatch(
        editInterview({
          interviewId: interview.interview_id,
          data: {
            scheduled_at,
            duration_minutes: durationMinutes,
            interview_mode: interviewMode,
            meeting_link: interviewMode === "video" ? interviewLink : undefined,
            meeting_location:
              interviewMode === "in_person" ? interviewLocation : undefined,
            meeting_platform: interviewMode === "phone" ? "phone" : undefined,
            interviewer_ids: interviewerIds,
            instructions: instructions || undefined,
            send_notification: sendEmail,
          },
        }),
      ).unwrap();

      // Refresh interview table
      dispatch(fetchInterviews({}));
      onClose();
    } catch (error) {
      console.error("Failed to edit interview:", error);
    } finally {
      setLoading(false);
    }
  };

  const Label = ({ text }: { text: string }) => (
    <div
      style={{
        fontWeight: 500,
        fontSize: 13,
        marginBottom: 6,
        color: "#374151",
      }}>
      {text}
    </div>
  );

  return (
    <Modal
      title="Edit Interview"
      open={show}
      onCancel={onClose}
      onOk={handleSubmit}
      confirmLoading={loading}
      okText="Save Changes"
      width={600}
      zIndex={1001}
      styles={{
        mask: {
          backdropFilter: "blur(6px)",
          WebkitBackdropFilter: "blur(6px)",
          background: "rgba(15, 23, 42, 0.4)",
        },
      }}>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          marginTop: 16,
        }}>
        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Label text="Interview Date" />
            <DatePicker
              style={{ width: "100%" }}
              value={interviewDate ? dayjs(interviewDate, "YYYY-MM-DD") : null}
              onChange={(d) =>
                setInterviewDate(d ? d.format("YYYY-MM-DD") : "")
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <Label text="Interview Time" />
            <TimePicker
              style={{ width: "100%" }}
              value={interviewTime ? dayjs(interviewTime, "HH:mm:ss") : null}
              onChange={(t) => setInterviewTime(t ? t.format("HH:mm:ss") : "")}
              format="HH:mm"
              minuteStep={15}
              showNow={false}
            />
          </div>
        </div>

        <div>
          <Label text="Duration (Minutes)" />
          <Select
            style={{ width: "100%" }}
            value={durationMinutes}
            onChange={setDurationMinutes}
            options={[
              { value: 15, label: "15 Minutes" },
              { value: 30, label: "30 Minutes" },
              { value: 45, label: "45 Minutes" },
              { value: 60, label: "60 Minutes (1 Hour)" },
              { value: 90, label: "90 Minutes (1.5 Hours)" },
              { value: 120, label: "120 Minutes (2 Hours)" },
            ]}
          />
        </div>

        <div style={{ display: "flex", gap: 16 }}>
          <div style={{ flex: 1 }}>
            <Label text="Interview Mode" />
            <Select
              style={{ width: "100%" }}
              value={interviewMode}
              onChange={(v) => {
                setInterviewMode(v as InterviewMode);
              }}
              options={[
                { value: "video", label: "Video Call" },
                { value: "in_person", label: "In Person" },
                { value: "phone", label: "Phone Screen" },
              ]}
            />
          </div>

          <div style={{ flex: 1 }}>
            {interviewMode === "video" && (
              <>
                <Label text="Meeting Link" />
                <Input
                  placeholder="https://zoom.us/..."
                  value={interviewLink}
                  onChange={(e) => setInterviewLink(e.target.value)}
                />
              </>
            )}
            {interviewMode === "in_person" && (
              <>
                <Label text="Meeting Location" />
                <Input
                  placeholder="Office Address / Room"
                  value={interviewLocation}
                  onChange={(e) => setInterviewLocation(e.target.value)}
                />
              </>
            )}
            {interviewMode === "phone" && (
              <>
                <Label text="Phone Number" />
                <Input
                  placeholder="+1 234 567 890"
                  value={interviewPhone}
                  onChange={(e) => setInterviewPhone(e.target.value)}
                />
              </>
            )}
          </div>
        </div>

        <div>
          <Label text="Assign Interviewers" />
          <Select
            mode="multiple"
            style={{ width: "100%" }}
            placeholder="Select interviewers..."
            value={interviewerIds}
            onChange={(vals: number[]) => setInterviewerIds(vals)}
            options={employeeOptions}
            loading={employeesLoading}
            filterOption={(input: string, option: any) =>
              (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
            }
            allowClear
            maxTagCount={3}
          />
        </div>

        <div>
          <Label text="Notes / Instructions for Candidate" />
          <TextArea
            rows={3}
            placeholder="E.g., Please bring your laptop, or prepare for technical whiteboard exercise..."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "12px",
            background: "#f8fafc",
            borderRadius: 8,
            border: "1px solid #e2e8f0",
          }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 500, color: "#1e293b" }}>
              Send Update Email
            </div>
            <div style={{ fontSize: 12, color: "#64748b" }}>
              Notify candidate of these changes via email
            </div>
          </div>
          <Switch checked={sendEmail} onChange={setSendEmail} />
        </div>
      </div>
    </Modal>
  );
};

export default EditInterviewModal;
